import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { IS_DEMO_MODE } from '../lib/demoMode';

interface User {
  user_id: string;
  email: string;
  role: string;
  tenant_id: string;
  created_at: number;
  is_onprem_enabled: boolean;
  legacy_products_enabled?: boolean;
  picture_url?: string;
  given_name?: string;
  family_name?: string;
}

// Synthetic user for the public demo builder: no session exists, so auth is
// bypassed entirely and pages relying on `user` render immediately.
const DEMO_USER: User = {
  user_id: 'demo',
  email: 'demo@usekestrel.ai',
  role: 'org_admin',
  tenant_id: 'demo',
  created_at: 0,
  is_onprem_enabled: false,
  given_name: 'Demo',
  family_name: 'User',
};

export function useAuth() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(IS_DEMO_MODE ? true : null);
  const [isLoading, setIsLoading] = useState(!IS_DEMO_MODE);
  const [user, setUser] = useState<User | null>(IS_DEMO_MODE ? DEMO_USER : null);

  const logout = async () => {
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    }
  };

  useEffect(() => {
    if (IS_DEMO_MODE) return;
    const checkAuth = async () => {
      try {
        const isValid = await apiClient.validateSession();
        if (isValid) {
          // Fetch user data if authenticated
          try {
            const userData = await apiClient.getCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
          } catch (userError) {
            console.error('Failed to fetch user data:', userError);
            setIsAuthenticated(false);
            navigate('/login');
          }
        } else {
          setIsAuthenticated(false);
          navigate('/login');
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  return { isAuthenticated, isLoading, user, logout };
} 